import { allocateSeats, divisorFor } from './sainteLague.js';
/**
 * Marginalanalys: för varje parti, hur många röster som skiljer till nästa mandat
 * respektive till att förlora ett mandat. Bygger på jämförelsetalen i den fördelning
 * som `allocateSeats` producerar för samma indata.
 */
export function analyzeMarginals(votesInput, opts) {
    const result = allocateSeats(votesInput, opts);
    const votesByParty = new Map(votesInput.map((p) => [p.party, p.votes]));
    const awarded = result.rounds.map((r) => ({
        party: r.party,
        quotient: r.quotient,
    }));
    const participating = Object.keys(result.seats).filter((p) => !result.excludedByThreshold.includes(p));
    // Nästa jämförelsetal per parti (om partiet skulle ta ett mandat till).
    const nextQuotient = new Map();
    for (const party of participating) {
        const v = votesByParty.get(party) ?? 0;
        const s = result.seats[party] ?? 0;
        nextQuotient.set(party, v / divisorFor(s, opts.firstDivisor));
    }
    return participating
        .map((party) => {
        const v = votesByParty.get(party) ?? 0;
        const s = result.seats[party] ?? 0;
        // --- Röster för nästa mandat ---
        // Billigast är att slå ut det svagaste mandatet som något annat parti håller.
        const othersAwarded = awarded.filter((a) => a.party !== party);
        let votesForNextSeat = 0;
        let seatTakenFrom = null;
        if (othersAwarded.length > 0) {
            const weakest = othersAwarded.reduce((min, a) => (a.quotient < min.quotient ? a : min));
            const myNextDivisor = divisorFor(s, opts.firstDivisor);
            const needed = Math.floor(weakest.quotient * myNextDivisor) + 1;
            votesForNextSeat = Math.max(0, needed - v);
            seatTakenFrom = votesForNextSeat > 0 ? weakest.party : null;
        }
        // --- Marginal till att förlora ett mandat ---
        let votesMarginToLoseSeat = Number.POSITIVE_INFINITY;
        let seatLostTo = null;
        if (s > 0) {
            // Starkaste utmanare = högsta "nästa jämförelsetal" bland övriga deltagare.
            let challengerQ = Number.NEGATIVE_INFINITY;
            let challenger = null;
            for (const other of participating) {
                if (other === party)
                    continue;
                const q = nextQuotient.get(other) ?? Number.NEGATIVE_INFINITY;
                if (q > challengerQ) {
                    challengerQ = q;
                    challenger = other;
                }
            }
            if (challenger !== null && Number.isFinite(challengerQ)) {
                const myWeakDivisor = divisorFor(s - 1, opts.firstDivisor);
                // Partiet behåller mandatet så länge v / myWeakDivisor > challengerQ.
                const floorVotes = Math.floor(challengerQ * myWeakDivisor) + 1;
                votesMarginToLoseSeat = Math.max(0, v - floorVotes);
                seatLostTo = challenger;
            }
        }
        return {
            party,
            seats: s,
            votesForNextSeat,
            seatTakenFrom,
            votesMarginToLoseSeat,
            seatLostTo,
        };
    })
        .sort((a, b) => a.votesForNextSeat - b.votesForNextSeat);
}
//# sourceMappingURL=marginal.js.map